import React, { useState } from 'react';

import styled from '@emotion/styled';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Table, Button, NavLinkButton, Modal, Alert, ButtonGroup } from '@shared/components/bootstrap';
import { Timestamp } from '@shared/components/helpers';

import { ConfirmModal } from '@admin/components/helpers/confirm_modal';
import { Panel } from '@admin/components/helpers/panel';
import { Permission } from '@admin/components/helpers/permission';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import { HIRING } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  Currency,
  useHiringJobListingsPaginatedQuery,
  useHiringJobListingDeactivateMutation,
  useHiringJobListingActivateMutation,
} from '@admin/schema';
import { warehouseOrDepotNameWithType } from '@admin/utils/warehouse_or_depots';

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

export const ErrorModal: React.FC<{
  errorMessage: string;
  onClose: () => void;
}> = ({ errorMessage, onClose }) => (
  <Modal onClose={onClose}>
    <Panel>
      <Panel.Header style={{ padding: '20px' }}>
        <h4>Something Went Wrong</h4>
      </Panel.Header>
      <Panel.Body>
        <Alert style="danger">{errorMessage}</Alert>
      </Panel.Body>
      <Panel.Footer>
        <ButtonContainer>
          <Button kind="primary" onClick={onClose}>
            OK
          </Button>
        </ButtonContainer>
      </Panel.Footer>
    </Panel>
  </Modal>
);

export const HiringJobListings: React.FC = () => {
  useTitle('Hiring | Job Listings');
  const [page, setPage] = useState<number | undefined>();
  const [confirmModal, setConfirmModal] = useState<{ message: string; onConfirm: () => void } | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>();
  const { data, loading, refetch } = useHiringJobListingsPaginatedQuery({ client, variables: { page: page } });
  const paginated = data?.paginated;

  const [activateJobListing] = useHiringJobListingActivateMutation({ client });
  const [deactivateJobListing] = useHiringJobListingDeactivateMutation({ client });

  const handleActivate = async (id: string) => {
    setConfirmModal({
      message: 'Are you sure you want to activate this job listing? This will make it visible to job seekers.',
      onConfirm: async () => {
        const result = await activateJobListing({ variables: { id } });

        const resultErrorMessage =
          result.errors && result.errors.length > 0 ? result.errors[0].message : result.data?.result.error;

        if (resultErrorMessage) {
          setErrorMessage(resultErrorMessage);
        } else {
          refetch();
        }

        setConfirmModal(undefined);
      },
    });
  };

  const handleDeactivate = async (id: string) => {
    setConfirmModal({
      message:
        'Are you sure you want to deactivate this job listing? This will make it no longer visible to job seekers.',
      onConfirm: async () => {
        await deactivateJobListing({ variables: { id } });
        refetch();
        setConfirmModal(undefined);
      },
    });
  };

  return (
    <Panel>
      <Table responsive striped>
        <thead>
          <tr>
            <th>Job Title</th>
            <th>Region</th>
            <th>Facility</th>
            <th>Hourly Rate</th>
            <th>Time Commitment</th>
            <th>Shift</th>
            <th>Workday Application</th>
            <th>Activated At</th>
            <th>Deactivated At</th>
            <th className="text-right">
              <Permission resource={HIRING.resource} action={HIRING.actions.update} denied={null}>
                <NavLinkButton to="/hiring/job_listings/new" kind="mint">
                  <FontAwesomeIcon icon={faPlus} /> New
                </NavLinkButton>
              </Permission>
            </th>
          </tr>
        </thead>
        <tbody>
          {paginated?.results.map((jobListing) => (
            <tr key={jobListing.id}>
              <td>{jobListing.job.title}</td>
              <td>{jobListing.region.name}</td>
              <td>{warehouseOrDepotNameWithType(jobListing.warehouseOrDepot)}</td>
              <td>
                {jobListing.hourlyRate.toLocaleString('en-US', {
                  style: 'currency',
                  currency: jobListing.region.currency === Currency.Usd ? 'USD' : 'CAD',
                })}
              </td>
              <td>{jobListing.fullTime ? 'Full-Time' : 'Part-Time'}</td>
              <td>{jobListing.nightShift ? 'Night Shift' : 'Daytime Shift'}</td>
              <td>
                {jobListing.workdayApplicationLink && (
                  <a
                    style={{ color: 'teal' }}
                    href={jobListing.workdayApplicationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View
                  </a>
                )}
              </td>
              <td>
                {jobListing.activatedAt ? (
                  <Timestamp value={jobListing.activatedAt} format={Timestamp.Format.DateTime} />
                ) : undefined}
              </td>
              <td>
                {jobListing.deactivatedAt ? (
                  <Timestamp value={jobListing.deactivatedAt} format={Timestamp.Format.DateTime} />
                ) : undefined}
              </td>
              <td className="text-right">
                <Permission resource={HIRING.resource} action={HIRING.actions.update} denied={null}>
                  <ButtonGroup>
                    {!jobListing.activatedAt && !jobListing.deactivatedAt && (
                      <Button kind="success" onClick={() => handleActivate(jobListing.id)}>
                        Activate
                      </Button>
                    )}
                    {jobListing.activatedAt && !jobListing.deactivatedAt && (
                      <Button kind="danger" onClick={() => handleDeactivate(jobListing.id)}>
                        Deactivate
                      </Button>
                    )}
                    <NavLinkButton to={`/hiring/job_listings/${jobListing.id}/edit`} kind="primary">
                      Edit
                    </NavLinkButton>
                  </ButtonGroup>
                </Permission>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {loading && <Spinner />}
      {paginated && <Pagination pagination={paginated.pagination} onPage={setPage} />}
      {confirmModal && <ConfirmModal {...confirmModal} onCancel={() => setConfirmModal(undefined)} />}
      {errorMessage && <ErrorModal errorMessage={errorMessage} onClose={() => setErrorMessage(undefined)} />}
    </Panel>
  );
};
